import React from "react";
import { Link } from "react-router-dom";
import { BrandMark } from "../brand/BrandMark";

/**
 * TopBar — issue #750 § 5.1. Three variants, one component: the approved
 * screens use all three (14 navy, the set-up screens white, sign-up none)
 * and none is a special case of another.
 *
 * The mark is `BrandMark` (`components/brand/BrandMark.jsx`, #1300), the one
 * new-look mark; this file draws no shape of its own and adds only the plain
 * "Compass" wordmark (16px, one weight, one colour, no tagline) beside it.
 *
 * #1428 (D385): the white and navy bars are full-width bands whose inner padding is `--nw-band-gutter`: the
 * 20px page gutter on a phone, and from md the room that puts the mark and the end controls one gutter inside
 * the edges of the 1040px column, whichever column the page below is in. `data-nw-band` is the hook index.css uses to cap
 * what sits beside a band and leave the band alone.
 */

/**
 * PR #887 review, B3 (Andreas's ruling, D160) — every end control's tap
 * area is exactly 44px wide (`--nw-size-tap`, #1118; 32 until then) and the FULL bar height, tiled edge to edge
 * with no overlap and no gap, replacing the earlier per-control negative
 * margins (which built 44px boxes that overlapped their neighbours —
 * found live: clicking near "Contrast" opened the account menu instead).
 *
 * THE ARITHMETIC THAT MAKES THIS WORK WITHOUT NEGATIVE MARGINS. This
 * wrapper gets an EXPLICIT height equal to the bar's own (54 navy, 50
 * white) — taller than the row's own content-box height (26, either
 * variant: padding 14 or 12 either side, and 14+26+14 / 12+26+12 both
 * equal the bar's own total height by construction, D140's own
 * arithmetic). A flex child taller than its container's content box,
 * under the container's OWN `align-items: center`, overflows the padding
 * band symmetrically — exactly 14px (navy) / 12px (white) each side,
 * which is exactly the padding it overflows into, so it reaches the bar's
 * true top and bottom edges without a single negative margin. Each
 * control inside this wrapper then gets NO explicit height of its own —
 * flexbox's own default `align-items: stretch` fills it to the wrapper's
 * full height for free.
 */
function EndControlsRow({ endControls, barHeight }) {
  // D250 (#1122): the bar carries no caption at any width. The page says where
  // you are (its heading, or the log flow's step eyebrow).
  if (endControls == null) return null;
  return (
    <div className="flex items-center" style={{ gap: 14, minWidth: 0 }}>
      {/* `flex` alone, deliberately no `items-center` — flexbox's own
          default cross-axis alignment is `stretch`, which is exactly what
          makes each 44px-wide control fill this div's FULL `barHeight`
          for free (see this component's own docstring above).
          `items-center` here would centre each control at its own
          natural content height instead — measured doing exactly that
          (26px, matching the account circle's own size, not the bar's
          54/50) before this was caught. */}
      {endControls != null && (
        // #1118 (D221 G4) — controls are `--nw-size-tap` (44px) wide, tiled
        // edge to edge with no gap, so 360px fits: 360 - 40 padding - 103
        // mark - 3x44 = 85px spare before any `rightLabel`. The empty
        // account circle compensates its own 6px (see AccountMenu.jsx).
        <div className="flex" style={{ height: barHeight, flexShrink: 0 }}>
          {endControls}
        </div>
      )}
    </div>
  );
}

export function TopBar({
  variant = "navy",
  // Issue #757 review round 2, R1 (Andreas's decision, 2026-09-24) — the
  // navy variant's own way to carry real, functional controls (not just
  // the caption `rightLabel` text) beside the wordmark. Today only
  // `LogGroundFlow.jsx` passes it (`AccessibilityMenu`/`AccountMenu`,
  // reused verbatim from `Layout.jsx` — see that file's own comment for
  // why this bar can't just borrow the shared white header instead:
  // `DirectionA1`/`FirstGround` draw a different composition). Rendered
  // OUTSIDE the `rightLabel` span so it never inherits that span's caption
  // styling.
  endControls = null,
  // PR #887 review, B4 — `SetUp.dc.html`/`CoachSetUp.dc.html` draw the
  // mark itself as `data-dc-id="topbar-logo"` (structure-check's own
  // finding: missing on the built SetUp screen), the same home link every
  // OTHER app screen's topbar carries (`Layout.jsx`'s hand-rolled header,
  // `data-testid="topbar-logo"`). `homeHref`/`homeLabel` are required in
  // practice — every real caller has an opinion about where its own logo
  // goes (`lib/homePath.js`'s whole reason to exist) — but default to
  // `null`/`undefined` rather than throwing, so a caller that hasn't been
  // updated yet degrades to a plain, unlinked mark instead of crashing.
  homeHref = null,
  homeLabel,
  className = "",
  "data-testid": testId,
  ...rest
}) {
  const barHeight = variant === "white" ? 50 : 54;
  const mark = (size, markVariant) => <BrandMark size={size} variant={markVariant} testId="topbar-mark" />;
  const wordmark = (color) => (
    <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 16, color }}>Compass</span>
  );
  // Issue #892 (D171 X2) — same reasoning as `NotificationsBell.jsx`'s own
  // `compactFocusRingFor`: `ring-ring` is navy (`index.css:91`), invisible
  // against this link's own `variant="navy"` bar, and this link keys off
  // `markVariant` ("dark" on the navy bar, "light" on the white one) rather
  // than a separate `surface` prop because the caller already computes it
  // for `BrandMark` two lines below. `outline-offset-[-2px]` draws inside
  // the box rather than a `ring`'s outward `box-shadow`, so it can never be
  // clipped by an ancestor's overflow.
  const logo = (size, markVariant, wordColor) =>
    homeHref != null ? (
      <Link
        to={homeHref}
        aria-label={homeLabel}
        data-testid="topbar-logo"
        // #931, D171 X2 ruling — `nw-topbar-focus-hc-navy` on the
        // non-dark (white bar) branch only, same "white on navy and navy
        // on white" per-surface split the non-hc outline colour above
        // already uses; see `NotificationsBell.jsx`'s own
        // `compactFocusRingFor` for the full reasoning.
        className={`flex items-center focus-visible:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] nw-topbar-focus ${
          markVariant === "dark" ? "focus-visible:outline-white" : "focus-visible:outline-[#0B2E59] nw-topbar-focus-hc-navy"
        } rounded-lg`}
        style={{ gap: 8, flexShrink: 0, minHeight: "var(--nw-size-tap)" }}
      >
        {mark(size, markVariant)}
        {wordmark(wordColor)}
      </Link>
    ) : (
      <div className="flex items-center" style={{ gap: 8, flexShrink: 0 }}>
        {mark(size, markVariant)}
        {wordmark(wordColor)}
      </div>
    );
  if (variant === "none") {
    return (
      <div
        data-testid={testId}
        className={`flex items-center ${className}`}
        style={{ gap: 8 }}
        {...rest}
      >
        <BrandMark size={24} variant="light" testId="topbar-mark" />
        <span
          style={{
            fontFamily: "'Manrope', sans-serif",
            fontWeight: 800,
            fontSize: 16,
            color: "var(--nw-navy)",
          }}
        >
          Compass
        </span>
      </div>
    );
  }

  if (variant === "white") {
    // PR #887 review, B1/R3 — this variant used to draw a DECORATIVE
    // bell/info/empty-avatar of its own (`OnboardingNew.jsx`'s own
    // docstring: "none of it wired to `NotificationsBell`/
    // `AccessibilityMenu`/the real `AccountMenu`"), which is why
    // `Layout.jsx` never used it for `/onboarding`/`/coach-setup` and
    // hand-rolled a second white header instead — the one the review
    // found still carrying the old multicolour brand mark and a bordered,
    // 44px Contrast ring (B1). Now takes `endControls` exactly like the
    // navy variant, so `Layout.jsx` can render THIS component with the
    // real controls and retire its own hand-rolled copy. `gap: 14` (not
    // the navy variant's row) matches `SetUp.dc.html:62`/
    // `CoachSetUp.dc.html:54`'s own drawn gap — B2's fix (see
    // `NotificationsBell.jsx`'s `compact`/`AccessibilityMenu.jsx`'s
    // `hideTextSize`/`AccountMenu.jsx`'s `empty`, all of which shrink
    // their own 44px tap target's LAYOUT footprint via negative margin)
    // is what keeps that gap meaningful instead of visually swallowed by
    // three un-shrunk 44px boxes. `position: sticky` (R3) matches the
    // header this variant now replaces (`Layout.jsx`'s shared `<header>`
    // was `sticky top-0 z-20`).
    //
    // `height: 50` — same reasoning as the navy variant's own pinned
    // height, and load-bearing here in a way it is only a floor for
    // there: each control's negative margin is a FIXED px value
    // shrinking a REM-sized (`h-11`/`w-11`, 2.75rem) box, so it only
    // cancels exactly at the 16px root `--nw-base-font` defaults to
    // NOT being set at (measured: this app's own default is 17px, which
    // makes `h-11` 46.75px, not 44 — the residual 2.75px per control is
    // what pushed the unpinned natural height to 53.75 against the drawn
    // 48–52 band). Pinning the height the same way the navy bar already
    // does keeps the drawn 12px padding + 26px circle arithmetic exact
    // regardless.
    // PR #887 review — a real `<header>` element, not a `<div>`: this
    // component replaces the app's ACTUAL page header (the hand-rolled
    // `<header>` `Layout.jsx` renders everywhere else), which carries the
    // implicit ARIA `banner` landmark. A `<div>` here silently dropped it —
    // found by `spacing-tokens.spec.js`'s own `measureHeaderPadding`
    // (`page.getByRole("banner")`, expecting exactly one), which is a real
    // regression for a screen-reader user navigating by landmark, not just
    // a test technicality.
    return (
      <header
        data-testid={testId}
        data-nw-band="topbar"
        className={`flex items-center justify-between ${className}`}
        style={{
          position: "sticky",
          top: 0,
          zIndex: 20,
          padding: "12px var(--nw-band-gutter)",
          gap: 8,
          height: 50,
          background: "var(--nw-surface)",
          borderBottom: "1px solid var(--nw-line)",
        }}
        {...rest}
      >
        {logo(24, "light", "var(--nw-navy)")}
        <EndControlsRow
          endControls={endControls}
          barHeight={barHeight}
        />
      </header>
    );
  }

  // "navy" — the default, 14 of 22 screens.
  //
  // #780 (D140) — `height: 54` is not an arbitrary round number: it is the
  // drawing's OWN arithmetic (`padding: 14px 20px` + the 26px account
  // circle, the tallest thing the artboards themselves draw = 14+26+14=54).
  // PR #887 review, B2's first cut fixed one overflow cause
  // (`NotificationsBell`/`AccessibilityMenu`/`AccountMenu` shrinking their
  // 44px tap targets via negative margin) but review round 3 (B3, D160)
  // replaced that technique entirely — see `EndControlsRow`'s own
  // docstring for the current one, which this `height` is now load-bearing
  // FOR (the wrapper it builds needs this exact number to compute its own
  // overflow correctly), not just a safety floor.
  //
  // R3 — `position: sticky; top: 0` matches the shared `<header>` this
  // component replaces on every screen that owns its page
  // (`ChromelessOverrideContext`) or that `Layout.jsx` swaps in for
  // (`drawnTopBar`): that header was `sticky top-0 z-20`, and this bar
  // stepping in for it silently dropped that behaviour until now.
  //
  // A real `<header>` element too (same reasoning as the white variant's
  // own comment above) — this is the ACTUAL page header on every screen
  // that renders it, and a `<div>` here silently dropped the implicit
  // `banner` landmark a screen-reader user navigates by.
  return (
    <header
      data-testid={testId}
      data-nw-band="topbar"
      data-nw-tone="navy"
      className={`flex items-center justify-between ${className}`}
      style={{ position: "sticky", top: 0, zIndex: 20, padding: "14px var(--nw-band-gutter)", gap: 8, height: barHeight, background: "var(--nw-navy)" }}
      {...rest}
    >
      {logo(24, "dark", "#FFFFFF")}
      <EndControlsRow endControls={endControls} barHeight={barHeight} />
    </header>
  );
}

export default TopBar;
